const CATEGORY_ORDER = {
  behavior: 0,
  sleep: 1,
  mood: 2,
  food: 3,
};

const CATEGORY_LABELS = {
  behavior: 'Behavior',
  sleep: 'Sleep',
  mood: 'Mood',
  food: 'Appetite',
};

const CHANGE_LABELS = {
  medication_added: 'Medication started',
  dose_changed: 'Dose changed',
  schedule_changed: 'Schedule changed',
  medication_stopped: 'Medication stopped',
};

const normalizeText = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();

const formatNumber = (value) => {
  if (!Number.isFinite(Number(value))) {
    return '';
  }

  const numeric = Number(value);
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1).replace(/\.0$/, '');
};

const formatDateLabel = (value = '') => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return normalizeText(value);
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const sortMedicationTimingPatterns = (patterns = []) => (
  [...(Array.isArray(patterns) ? patterns : [])].sort((left, right) => {
    const leftOrder = CATEGORY_ORDER[left?.patternCategory] ?? 99;
    const rightOrder = CATEGORY_ORDER[right?.patternCategory] ?? 99;
    return leftOrder - rightOrder
      || normalizeText(left?.patternCategory).localeCompare(normalizeText(right?.patternCategory))
      || normalizeText(left?.medicationChange?.entryId).localeCompare(normalizeText(right?.medicationChange?.entryId));
  })
);

export const getMedicationTimingPatternCategoryLabel = (category = '') => (
  CATEGORY_LABELS[normalizeText(category).toLowerCase()] || normalizeText(category)
);

export const getMedicationTimingPatternChangeLabel = (medicationChangeType = '') => (
  CHANGE_LABELS[normalizeText(medicationChangeType).toLowerCase()] || 'Medication change'
);

export const formatMedicationTimingPatternContext = ({ medicationChangeType = '', medicationName = '', effectiveAt = '' } = {}) => {
  const changeLabel = getMedicationTimingPatternChangeLabel(medicationChangeType);
  const nameLabel = normalizeText(medicationName);
  const dateLabel = formatDateLabel(effectiveAt);

  return [changeLabel, nameLabel ? `for ${nameLabel}` : '', dateLabel ? `on ${dateLabel}` : '']
    .filter(Boolean)
    .join(' ')
    .trim();
};

export const formatMedicationTimingPatternRowSummary = (pattern = {}) => {
  const category = normalizeText(pattern?.patternCategory).toLowerCase();
  const before = pattern?.comparison?.before?.metrics || {};
  const after = pattern?.comparison?.after?.metrics || {};

  if (category === 'behavior') {
    const beforeSeverity = normalizeText(before.dominantSeverityBucket || 'unknown');
    const afterSeverity = normalizeText(after.dominantSeverityBucket || 'unknown');
    return `Severity ${beforeSeverity} → ${afterSeverity}`;
  }

  if (category === 'sleep') {
    const beforeDuration = formatNumber(before.medianDurationMinutes);
    const afterDuration = formatNumber(after.medianDurationMinutes);
    const beforeQuality = normalizeText(before.dominantQuality || 'unknown');
    const afterQuality = normalizeText(after.dominantQuality || 'unknown');
    return `Median duration ${beforeDuration ? `${beforeDuration} min` : 'unknown'} → ${afterDuration ? `${afterDuration} min` : 'unknown'} · Quality ${beforeQuality} → ${afterQuality}`;
  }

  if (category === 'mood') {
    const beforeMood = normalizeText(before.dominantMood || 'unknown');
    const afterMood = normalizeText(after.dominantMood || 'unknown');
    return `Mood ${beforeMood} → ${afterMood}`;
  }

  const beforeAppetite = normalizeText(before.dominantAppetite || 'unknown');
  const afterAppetite = normalizeText(after.dominantAppetite || 'unknown');
  return `Appetite ${beforeAppetite} → ${afterAppetite}`;
};

export const formatMedicationTimingPatternCounts = (pattern = {}) => {
  const beforeCount = Number(pattern?.comparison?.before?.count || 0);
  const afterCount = Number(pattern?.comparison?.after?.count || 0);
  return `${beforeCount} before · ${afterCount} after`;
};

export const formatMedicationTimingPatternEvidenceValue = (patternCategory = '', evidence = {}) => {
  const category = normalizeText(patternCategory).toLowerCase();
  const fields = evidence?.fields || {};

  if (category === 'behavior') {
    const severity = fields.severity;
    const severityLabel = normalizeText(fields.severityLabel || '');
    return [severity != null ? `Severity ${severity}` : '', severityLabel].filter(Boolean).join(' · ');
  }

  if (category === 'sleep') {
    const sleepDetails = fields.sleepDetails || {};
    const duration = sleepDetails.duration;
    const quality = normalizeText(sleepDetails.quality || '');
    return [duration != null ? `Duration ${duration} min` : '', quality ? `Quality ${quality}` : '']
      .filter(Boolean)
      .join(' · ');
  }

  if (category === 'mood') {
    return `Mood ${normalizeText(fields.moodValue || 'unknown')}`;
  }

  const foodDetails = fields.foodDetails || {};
  const portion = normalizeText(foodDetails.portion || '');
  const reaction = normalizeText(foodDetails.reaction || '');
  return [portion ? `Portion ${portion}` : '', reaction ? `Reaction ${reaction}` : '']
    .filter(Boolean)
    .join(' · ');
};

export const formatMedicationTimingPatternEvidenceTime = (occurredAt = '') => {
  const date = new Date(occurredAt);
  if (Number.isNaN(date.getTime())) {
    return normalizeText(occurredAt);
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const getMedicationTimingPatternEvidenceSections = (pattern = {}) => ([
  {
    key: 'before',
    label: 'Before change',
    items: Array.isArray(pattern?.evidence?.before) ? pattern.evidence.before : [],
  },
  {
    key: 'after',
    label: 'After change',
    items: Array.isArray(pattern?.evidence?.after) ? pattern.evidence.after : [],
  },
]);
